/**
 * What does the tenant have to do next, anywhere in their dashboard (COL-368).
 *
 * PR #1387 put a pulsing "!" on the Check-in/out tab whenever a handover
 * protocol was open. The follow-up ask was to widen that from one domain to
 * "the next user behaviour" generally: whatever is actually open — a contract
 * waiting for a signature, an unpaid deposit, overdue rent, an unfilled
 * protocol, an unread support reply, a referral reward that cannot be
 * credited — is what the strip should point at.
 *
 * This module is the single answer to that question. It is deliberately pure:
 * every fact it takes is already derived somewhere in `Dashboard.tsx` for the
 * card that renders it, so the pip on a tab and the card inside that tab read
 * from the same source and cannot disagree. Nothing here fetches, and nothing
 * here decides *whether* a fact is true — only what it means for the strip.
 *
 * The protocol half stays in `protocol-action.ts`; this file consumes its
 * answer rather than re-deriving it.
 */

export type NextActionTab =
  | "contract"
  | "payments"
  | "checkin"
  | "maintenance"
  | "referrals";

export type NextActionKind =
  /** A released contract is waiting for the tenant's signature. */
  | "sign"
  /** The deposit is (partly) unpaid. */
  | "deposit"
  /** A rent row is past its due date and unpaid. */
  | "overdue"
  /** The move-in handover protocol is open — see `protocol-action.ts`. */
  | "move_in"
  /** The move-out handover protocol is open. */
  | "move_out"
  /**
   * The move-out report is filed and the keys are still out.
   *
   * `key_return_pending` on the journey ladder: the walkthrough happened, so
   * nothing about the protocol is open any more, but the tenancy cannot be
   * settled — and the deposit cannot be released — while we do not have the
   * keys back. Neel asked for the reminder (23 Oct).
   */
  | "key_return"
  /** Unread replies on the tenant's support requests. */
  | "support"
  /** A referral reward is earned but has nothing to be credited against. */
  | "referral_reward"
  /**
   * Move-in is still ahead and nothing else is outstanding.
   *
   * The only kind here that is NOT a task. For a tenant who has signed, paid
   * and is simply waiting, "the next step" genuinely is moving in — and the
   * card otherwise fell through to "all done", which is true but tells a
   * tenant three days from their move-in nothing at all. Ranked LAST so it can
   * never displace something they actually have to act on.
   */
  | "move_in_upcoming";

export type TenantNextAction = {
  tab: NextActionTab;
  kind: NextActionKind;
  /**
   * Set only for kinds whose pip shows a number instead of "!" (support
   * today). Absent means the pip renders "!".
   */
  count?: number;
};

/**
 * The facts the strip is derived from. Each one already drives a card
 * somewhere in the tenant dashboard:
 *
 * - `contractAwaitingSignature` — `isContractSigningStage`, the same flag that
 *   shows the "Open in DocuSign" button under Next step.
 * - `depositDue` — `hasContractDepositDueNotification`, which already put a
 *   (non-pulsing) red dot on Contract before this change.
 * - `rentOverdue` — `paymentStatusLabel === "Overdue"`, the badge on the Next
 *   payment card. Deliberately NOT "rent is unpaid": an unpaid but not-yet-due
 *   month is the normal state for most of every month, and a pip that is on
 *   almost always is not a signal. The card says "Upcoming" quietly until the
 *   due date passes.
 * - `protocolKind` — `computeProtocolActionNeeded(...)?.kind`.
 * - `supportUnread` — `tenantSupportUnreadTotal`, the existing unread counter.
 * - `referralRewardStalled` — `reward_stalled_count` from `my_referrals`. Note
 *   this is the one referral state the tenant can act on: the panel already
 *   tells them in words to get in touch, because a reward with no upcoming
 *   rent to land on will otherwise wait forever. A referral merely advancing
 *   from applied to approved to signed is news, not a task, and gets no pip.
 */
export type TenantActionFacts = {
  contractAwaitingSignature: boolean;
  depositDue: boolean;
  rentOverdue: boolean;
  protocolKind: "move_in" | "move_out" | null;
  /**
   * The journey stage is `key_return_pending` — derived, not a new column.
   * Independent of `protocolKind`, which has already gone quiet by then.
   */
  keyReturnPending?: boolean;
  supportUnread: number;
  referralRewardStalled: number;
  /**
   * Move-in is in the future for a tenancy that is otherwise in order.
   *
   * Deliberately NOT "has a move-in date": a tenant who already moved in has
   * one too, and a past date must not produce an upcoming-move-in step.
   */
  moveInUpcoming?: boolean;
};

/**
 * Which action is "the" next one when several are open, for the single-line
 * summary. Money the tenant owes first, because it is the only kind with a
 * consequence attached; then the paperwork that has a closing window; then a
 * conversation; then money owed TO the tenant, which nothing expires on.
 *
 * This orders the summary only. The pips themselves are not ranked — every tab
 * with something open pulses, because a tenant with overdue rent AND an
 * unfilled protocol has to deal with both, and hiding one behind the other
 * just means they find out about it later.
 */
export const NEXT_ACTION_PRIORITY: readonly NextActionKind[] = [
  "sign",
  "deposit",
  "overdue",
  "move_in",
  "move_out",
  "key_return",
  "support",
  "referral_reward",
  // Last: informational, never ahead of something to act on.
  "move_in_upcoming",
] as const;

function priorityOf(kind: NextActionKind): number {
  const i = NEXT_ACTION_PRIORITY.indexOf(kind);
  return i === -1 ? NEXT_ACTION_PRIORITY.length : i;
}

/**
 * Everything the tenant has open right now, most important first.
 *
 * Returns an empty array when there is nothing to do — the quiet state, which
 * is what most tenants see most of the time and the state this whole feature
 * has to return to cleanly.
 */
export function computeTenantNextActions(
  facts: TenantActionFacts
): TenantNextAction[] {
  const actions: TenantNextAction[] = [];

  if (facts.contractAwaitingSignature)
    actions.push({ tab: "contract", kind: "sign" });
  if (facts.depositDue) actions.push({ tab: "contract", kind: "deposit" });
  if (facts.rentOverdue) actions.push({ tab: "payments", kind: "overdue" });
  if (facts.protocolKind)
    actions.push({ tab: "checkin", kind: facts.protocolKind });
  if (facts.keyReturnPending)
    actions.push({ tab: "checkin", kind: "key_return" });

  // Guard against a NaN or negative count reaching the badge: `Number(...)`
  // on a failed query yields NaN, and `NaN > 0` is false, but an explicit
  // check keeps the intent obvious at the call site.
  const unread = Number(facts.supportUnread);
  if (Number.isFinite(unread) && unread > 0)
    actions.push({ tab: "maintenance", kind: "support", count: unread });

  const stalled = Number(facts.referralRewardStalled);
  if (Number.isFinite(stalled) && stalled > 0)
    actions.push({ tab: "referrals", kind: "referral_reward" });
  /*
    Last, and only ever ALONGSIDE the others -- the ranking keeps it behind
    anything the tenant must act on, so it surfaces exactly when the card
    would otherwise say "all done" to somebody days from their move-in.
  */
  if (facts.moveInUpcoming)
    actions.push({ tab: "checkin", kind: "move_in_upcoming" });

  return actions.sort((a, b) => priorityOf(a.kind) - priorityOf(b.kind));
}

/**
 * The same list keyed by tab, for the strip: one lookup per tab rather than a
 * scan per tab. A tab can hold more than one action — Contract carries both
 * "sign" and "deposit" — and they stay in priority order within the tab, so
 * the pip and the drawer chip describe the more urgent one first.
 */
export function tenantActionsByTab(
  actions: TenantNextAction[]
): Partial<Record<NextActionTab, TenantNextAction[]>> {
  const byTab: Partial<Record<NextActionTab, TenantNextAction[]>> = {};
  for (const action of actions) {
    const list = byTab[action.tab];
    if (list) list.push(action);
    else byTab[action.tab] = [action];
  }
  return byTab;
}

type Lang = "en" | "de";

/**
 * The trailing chip on a mobile drawer row: two or three words naming what is
 * open, in the same vocabulary the tab itself uses.
 */
export function nextActionChipLabel(
  kind: NextActionKind,
  language: Lang
): string {
  const en: Record<NextActionKind, string> = {
    sign: "Sign",
    deposit: "Deposit",
    overdue: "Overdue",
    move_in: "Move-in",
    move_out: "Move-out",
    key_return: "Keys",
    support: "Unread",
    referral_reward: "Reward",
    // Not a task, so the chip names the event rather than an instruction.
    move_in_upcoming: "Move-in",
  };
  const de: Record<NextActionKind, string> = {
    sign: "Unterschreiben",
    deposit: "Kaution",
    overdue: "Überfällig",
    move_in: "Einzug",
    move_out: "Auszug",
    key_return: "Schlüssel",
    support: "Ungelesen",
    referral_reward: "Prämie",
    move_in_upcoming: "Einzug",
  };
  return language === "de" ? de[kind] : en[kind];
}

/**
 * What a screen reader hears on the pip. A pip is announced as a status, so
 * this has to say what needs doing, not just that something does — "1 unread"
 * on its own tells a sighted user plenty and a screen-reader user nothing.
 */
export function nextActionAriaLabel(
  action: TenantNextAction,
  language: Lang
): string {
  const de = language === "de";
  switch (action.kind) {
    case "sign":
      return de
        ? "Ihr Vertrag wartet auf Ihre Unterschrift"
        : "Your contract is waiting for your signature";
    case "deposit":
      return de ? "Ihre Kaution ist offen" : "Your deposit is still open";
    case "overdue":
      return de ? "Ihre Miete ist überfällig" : "Your rent is overdue";
    case "move_in":
      return de
        ? "Ihr Einzugsprotokoll braucht Ihre Aufmerksamkeit"
        : "Your move-in protocol needs your attention";
    case "move_out":
      return de
        ? "Ihr Auszugsprotokoll braucht Ihre Aufmerksamkeit"
        : "Your move-out protocol needs your attention";
    case "move_in_upcoming":
      return de ? "Ihr Einzug steht bevor" : "Your move-in is coming up";
    case "key_return":
      return de
        ? "Ihre Schlüssel müssen noch zurückgegeben werden"
        : "Your keys still have to be returned";
    case "support": {
      const n = action.count ?? 0;
      if (de)
        return n === 1
          ? "1 ungelesene Support-Antwort"
          : `${n} ungelesene Support-Antworten`;
      return n === 1
        ? "1 unread support update"
        : `${n} unread support updates`;
    }
    case "referral_reward":
      return de
        ? "Eine Empfehlungsprämie muss geklärt werden"
        : "A referral reward needs sorting out";
  }
}

/**
 * The one-line "Next up" summary: the highest-priority open action, phrased as
 * the thing to do rather than the state it is in.
 */
export function nextActionSummary(
  action: TenantNextAction,
  language: Lang
): string {
  const de = language === "de";
  switch (action.kind) {
    case "sign":
      return de ? "Vertrag unterschreiben" : "Sign your contract";
    case "deposit":
      return de ? "Kaution zahlen" : "Pay your deposit";
    case "overdue":
      return de ? "Miete ist überfällig" : "Rent is overdue";
    case "move_in":
      return de
        ? "Einzugsprotokoll ausfüllen"
        : "Fill in your move-in protocol";
    case "move_out":
      return de
        ? "Auszugsprotokoll ausfüllen"
        : "Fill in your move-out protocol";
    case "key_return":
      return de ? "Schlüssel zurückgeben" : "Return your keys";
    case "support":
      return de ? "Antwort vom Support lesen" : "Read the reply from support";
    case "referral_reward":
      return de ? "Empfehlungsprämie klären" : "Sort out your referral reward";
  }
}
